import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { G, FONT_FAMILY, OVERVIEW } from "../../constants/theme";
import OverviewSection from "../overview/OverviewSection";

// ── Status / priority / type vocabularies — reuse the app's existing values,
//    no new classification. ────────────────────────────────────────────────────
const DONE_STATUSES = ["Done", "Closed", "Resolved", "Won't Do"];
const BLOCKED_STATUSES = ["On Hold", "Blocked"];
const PRIORITY_ORDER = ["Highest", "High", "Medium", "Low", "Lowest"];
// "Critical" and "Highest" are the same tier across the codebase (workload.routes).
const HIGHEST_ALIASES = ["Highest", "Critical"];

function sum(map, keys) {
  return keys.reduce((acc, k) => acc + (map[k] || 0), 0);
}
function pct(n, total) {
  return total > 0 ? Math.round((n / total) * 100) : 0;
}

function EyebrowLabel({ children, style }) {
  return <div style={{ ...OVERVIEW.eyebrow, color: G.gray500, ...style }}>{children}</div>;
}

// One connected stat strip — reads as a single command-center bar, not six cards.
function StatStrip({ tiles, loading, total, pctComplete }) {
  const [hov, setHov] = useState(null);
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
        border: `1px solid ${OVERVIEW.cardBorder}`,
        borderRadius: OVERVIEW.panelRadius,
        overflow: "hidden",
      }}
    >
      {tiles.map((t, i) => {
        const isHov = hov === i;
        return (
          <div
            key={t.label}
            onMouseEnter={() => setHov(i)}
            onMouseLeave={() => setHov(null)}
            style={{
              padding: "16px 18px",
              minWidth: 0,
              borderLeft: i === 0 ? "none" : `1px solid ${G.border}`,
              background: isHov ? G.brandSubtle : G.white,
              transition: "background 0.12s",
            }}
          >
            <div style={{ fontSize: 28, fontWeight: 700, color: G.gray900, letterSpacing: "-0.02em", lineHeight: 1 }}>
              {loading ? "—" : t.value}
            </div>
            <div style={{ ...OVERVIEW.eyebrow, color: G.gray500, marginTop: 8 }}>{t.label}</div>
            {!loading && (
              <div style={{ fontSize: 11, color: isHov ? G.gray600 : G.gray400, marginTop: 4 }}>
                {pct(t.value, total)}% of total
              </div>
            )}
          </div>
        );
      })}

      {/* % Complete — the anchor metric, brand-tinted */}
      <div
        style={{
          padding: "16px 18px",
          minWidth: 0,
          borderLeft: `1px solid ${G.border}`,
          background: G.brandSubtle,
        }}
      >
        <div style={{ fontSize: 28, fontWeight: 700, color: G.blue700, letterSpacing: "-0.02em", lineHeight: 1 }}>
          {loading ? "—" : `${pctComplete}%`}
        </div>
        <div style={{ ...OVERVIEW.eyebrow, color: G.blue700, opacity: 0.8, marginTop: 8 }}>% Complete</div>
        <div style={{ height: 5, width: "100%", background: G.white, borderRadius: 999, overflow: "hidden", marginTop: 9 }}>
          <div style={{ height: "100%", width: `${loading ? 0 : pctComplete}%`, background: G.brand, borderRadius: 999, transition: "width 0.3s" }} />
        </div>
      </div>
    </div>
  );
}

function BreakdownRow({ name, count, total, max, accent, emphasise, loading }) {
  const [hov, setHov] = useState(false);
  const barW = max > 0 ? Math.max((count / max) * 100, count > 0 ? 3 : 0) : 0;
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: "grid",
        gridTemplateColumns: "104px 1fr auto",
        alignItems: "center",
        gap: 12,
        padding: "9px 10px",
        borderRadius: OVERVIEW.panelRadius,
        background: hov ? G.brandSubtle : "transparent",
        transition: "background 0.12s",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
        {accent && <span style={{ width: 7, height: 7, borderRadius: "50%", background: accent, flexShrink: 0 }} />}
        <span
          style={{
            fontSize: 13,
            fontWeight: emphasise ? 700 : 500,
            color: emphasise ? G.gray900 : G.gray700,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {name}
        </span>
      </div>

      <div style={{ height: 8, width: "100%", background: G.blue50, borderRadius: 999, overflow: "hidden" }}>
        <div
          style={{
            height: "100%",
            width: `${loading ? 0 : barW}%`,
            background: G.brand,
            opacity: emphasise ? 1 : 0.68,
            borderRadius: 999,
            transition: "width 0.3s",
          }}
        />
      </div>

      <div style={{ display: "flex", alignItems: "baseline", gap: 8, justifyContent: "flex-end" }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: G.gray900, minWidth: 28, textAlign: "right" }}>
          {loading ? "—" : count}
        </span>
        <span style={{ fontSize: 11, color: hov ? G.gray600 : G.gray400, width: 34, textAlign: "right" }}>
          {loading ? "" : `${pct(count, total)}%`}
        </span>
      </div>
    </div>
  );
}

function BreakdownPanel({ title, children }) {
  return (
    <div
      style={{
        minWidth: 0,
        border: `1px solid ${OVERVIEW.panelBorder}`,
        borderRadius: OVERVIEW.panelRadius,
        padding: "16px 16px 12px",
        background: G.white,
      }}
    >
      <EyebrowLabel style={{ marginBottom: 8 }}>{title}</EyebrowLabel>
      {children}
    </div>
  );
}

// ── Section ──────────────────────────────────────────────────────────────────
export default function PersonalDeveloperSummary({
  loading = false,
  total = 0,
  byStatus = null,
  byPriority = null,
  byType = null,
  selectedDeveloper = "",
}) {
  const navigate = useNavigate();

  const sMap = byStatus || {};
  const pMap = byPriority || {};
  const tMap = byType || {};

  const done = sum(sMap, DONE_STATUSES);
  const pctComplete = pct(done, total);

  const statusTiles = [
    { label: "Total Tickets", value: total },
    { label: "Done", value: done },
    { label: "Testing", value: sMap["Testing"] || 0 },
    { label: "To Do", value: sMap["To Do"] || 0 },
    { label: "Blocked", value: sum(sMap, BLOCKED_STATUSES) },
  ];

  // Priority rows — always the 5 canonical tiers; Critical folded into Highest.
  const priorityRows = PRIORITY_ORDER.map((name) => ({
    name,
    count: name === "Highest" ? sum(pMap, HIGHEST_ALIASES) : (pMap[name] || 0),
  }));
  const priorityUnknown = pMap["Unknown"] || 0;
  const priorityMax = Math.max(...priorityRows.map((r) => r.count), 1);

  // Work-type rows — real issue types from the data; Bug pinned first & flagged.
  const typeEntries = Object.entries(tMap)
    .filter(([k, v]) => k !== "Unknown" && v > 0)
    .sort((a, b) => b[1] - a[1]);
  const bug = typeEntries.find(([k]) => k.toLowerCase() === "bug");
  const rest = typeEntries.filter(([k]) => k.toLowerCase() !== "bug");
  const typeRows = [
    ...(bug ? [{ name: bug[0], count: bug[1], isBug: true }] : [{ name: "Bug", count: 0, isBug: true }]),
    ...rest.slice(0, 5).map(([name, count]) => ({ name, count, isBug: false })),
  ];
  const typeUnknown = tMap["Unknown"] || 0;
  const typeMax = Math.max(...typeRows.map((r) => r.count), 1);

  const who = selectedDeveloper || "All developers";

  return (
    <OverviewSection background="page">
      <div
        data-tour="developer-snapshot"
        style={{
          background: G.white,
          border: `1px solid ${OVERVIEW.cardBorder}`,
          borderRadius: OVERVIEW.cardRadius,
          boxShadow: OVERVIEW.cardShadow,
          overflow: "hidden",
          fontFamily: FONT_FAMILY,
        }}
      >
        {/* brand accent — ties the snapshot to the action cards below it */}
        <div style={{ height: 3, background: G.brand }} />

        <div style={{ padding: "28px 32px" }}>
          {/* Header row: identity + CTA */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 24, flexWrap: "wrap" }}>
            <div style={{ minWidth: 0 }}>
              <EyebrowLabel>Personal Overview</EyebrowLabel>
              <h2 style={{ ...OVERVIEW.title, color: G.gray900, margin: "10px 0 0" }}>Developer Snapshot</h2>
              <p style={{ ...OVERVIEW.support, color: G.gray600, margin: "10px 0 0", maxWidth: 640 }}>
                <strong style={{ color: G.gray900, fontWeight: 700 }}>{loading ? "…" : who}</strong>
                {" — how much work exists, what state it’s in, its priority mix, and what kind of work it is."}
              </p>
            </div>

            <button
              type="button"
              onClick={() => navigate("/lifecycle")}
              style={{
                flexShrink: 0,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "9px 16px",
                borderRadius: OVERVIEW.panelRadius,
                border: `1px solid ${OVERVIEW.cardBorder}`,
                background: G.white,
                color: G.brand,
                fontSize: 13,
                fontWeight: 600,
                fontFamily: FONT_FAMILY,
                cursor: "pointer",
                whiteSpace: "nowrap",
                transition: "background 0.12s, border-color 0.12s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = G.brandSubtle; e.currentTarget.style.borderColor = G.brand; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = G.white; e.currentTarget.style.borderColor = OVERVIEW.cardBorder; }}
            >
              View Detailed Lifecycle
              <span aria-hidden="true">→</span>
            </button>
          </div>

          <div style={{ height: 1, background: G.border, margin: "22px 0" }} />

          {/* Status summary */}
          <EyebrowLabel style={{ marginBottom: 12 }}>Status Summary</EyebrowLabel>
          <StatStrip tiles={statusTiles} loading={loading} total={total} pctComplete={pctComplete} />

          <div style={{ height: 1, background: G.border, margin: "22px 0" }} />

          {/* Priority + Work Type */}
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 24 }}>
            <BreakdownPanel title="Priority Breakdown">
              {priorityRows.map((r, i) => (
                <BreakdownRow
                  key={r.name}
                  name={r.name}
                  count={r.count}
                  total={total}
                  max={priorityMax}
                  loading={loading}
                  emphasise={i === 0}
                  accent={i === 0 ? G.brand : null}
                />
              ))}
              {!loading && priorityUnknown > 0 && (
                <BreakdownRow name="Unspecified" count={priorityUnknown} total={total} max={priorityMax} loading={loading} />
              )}
            </BreakdownPanel>

            <BreakdownPanel title="Work Type">
              {typeRows.map((r) => (
                <BreakdownRow
                  key={r.name}
                  name={r.name}
                  count={r.count}
                  total={total}
                  max={typeMax}
                  loading={loading}
                  emphasise={r.isBug}
                  accent={r.isBug ? G.red600 : null}
                />
              ))}
              {!loading && typeUnknown > 0 && (
                <BreakdownRow name="Unspecified" count={typeUnknown} total={total} max={typeMax} loading={loading} />
              )}
            </BreakdownPanel>
          </div>
        </div>
      </div>
    </OverviewSection>
  );
}
